import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  CreatePipelineBody,
  FreightDocument,
  FreightPipeline,
  FreightProviderHealth,
  FreightShipment,
  FreightShipmentDetail,
  FreightStatus,
  OpenPOOption,
  PipelineStage,
  QuoteRequestBody,
  RawVendorPORow,
  TenderRequestBody,
} from './types'

const PIPELINE_KEY = ['logistics', 'pipeline'] as const

/**
 * Per-provider health for every registered SPI Freight provider. Public
 * probe endpoint. Powers the per-provider status chips in the page header.
 */
export function useFreightHealth() {
  return useQuery({
    queryKey: ['logistics', 'freight-health'],
    queryFn: () => jsonFetch<FreightProviderHealth[]>('/api/freight/health'),
    staleTime: 60_000,
  })
}

/**
 * `statusFilter` values for {@link useFreightShipments}:
 *   - `undefined` (default) — active shipments only (server default `active=true`)
 *   - `'all'` — every status, including terminal (delivered/exception/cancelled)
 *   - a specific `FreightStatus` — that status only
 *
 * Terminal shipments (e.g. `delivered`) are excluded unless explicitly asked
 * for — without this, a PO with an already-registered, already-delivered
 * shipment is invisible in the default view, which is what led an operator
 * to attempt a duplicate "Register shipment" (crashed with a 500 before the
 * duplicate-detection fix in services/freight/store.py).
 */
export type ShipmentStatusFilter = FreightStatus | 'all'

/**
 * Shipments across ALL providers — the unified freight_shipments store.
 * Refetches every 30s while the page is open so tracking events feel live
 * without operator action. Provider + status filters are applied server-side.
 */
export function useFreightShipments(provider?: string, statusFilter?: ShipmentStatusFilter) {
  const params = new URLSearchParams()
  if (provider) params.set('provider', provider)
  if (statusFilter === 'all') {
    params.set('active', 'false')
  } else if (statusFilter) {
    params.set('status', statusFilter)
  }
  const qs = params.toString() ? `?${params.toString()}` : ''
  return useQuery({
    queryKey: ['logistics', 'shipments', provider ?? 'all', statusFilter ?? 'active'],
    queryFn: () => jsonFetch<FreightShipment[]>(`/api/freight/shipments${qs}`),
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}

/**
 * Single shipment detail — includes tracking_history/document_urls not
 * present on the flat list rows. Used by the pipeline work view once a
 * load has been tendered (freight_shipment_id set).
 */
export function useFreightShipmentDetail(shipmentId: number | null) {
  return useQuery({
    queryKey: ['logistics', 'shipment-detail', shipmentId],
    queryFn: () => jsonFetch<FreightShipmentDetail>(`/api/freight/shipments/${shipmentId}`),
    enabled: shipmentId != null,
    staleTime: 15_000,
  })
}

/**
 * Capability-aware document list for a tendered shipment (BOL, POD, etc).
 * Some providers (TQL) have no documents capability — the backend
 * returns a typed 501 in that case, surfaced here as a query error.
 */
export function useFreightDocuments(shipmentId: number | null) {
  return useQuery({
    queryKey: ['logistics', 'shipment-documents', shipmentId],
    queryFn: () => jsonFetch<FreightDocument[]>(`/api/freight/shipments/${shipmentId}/documents`),
    enabled: shipmentId != null,
    staleTime: 30_000,
    retry: false,
  })
}

// ─── Freight Pipeline (Logistics overhaul Phase 2) ──────────────────

/**
 * All pipeline rows (optionally filtered to one stage) — the kanban
 * board's data source. Refetches every 30s so operator-driven and
 * reconciler-driven stage moves show up without a manual refresh.
 */
export function useFreightPipelineList(stage?: PipelineStage) {
  const qs = stage ? `?stage=${encodeURIComponent(stage)}` : ''
  return useQuery({
    queryKey: [...PIPELINE_KEY, 'list', stage ?? 'all'],
    queryFn: () => jsonFetch<FreightPipeline[]>(`/api/freight/pipeline${qs}`),
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}

export function useFreightPipelineDetail(pipelineId: string | null) {
  return useQuery({
    queryKey: [...PIPELINE_KEY, 'detail', pipelineId],
    queryFn: () => jsonFetch<FreightPipeline>(`/api/freight/pipeline/${pipelineId}`),
    enabled: !!pipelineId,
    staleTime: 5_000,
  })
}

export function useTransitionPipeline() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { pipelineId: string; targetStage: PipelineStage; note?: string }) =>
      jsonFetch<FreightPipeline>(`/api/freight/pipeline/${vars.pipelineId}/transition`, {
        method: 'POST',
        body: { target_stage: vars.targetStage, note: vars.note },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_KEY })
    },
  })
}

/**
 * Request carrier quotes for a pipeline row — single-provider per call (v1;
 * fan-out comparison across providers in one request is a later backend
 * enhancement, see services/freight/router.py's quote() docstring).
 * Snapshots the response into `quote_options` and advances to `quoted`.
 */
export function useQuotePipeline() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { pipelineId: string; body: QuoteRequestBody }) =>
      jsonFetch<FreightPipeline>(`/api/freight/pipeline/${vars.pipelineId}/quote`, {
        method: 'POST',
        body: vars.body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_KEY })
    },
  })
}

/**
 * Book a rate — the money-committing action. Requires the
 * `can_tender_freight` capability server-side (require_freight_tender);
 * the UI additionally gates the button on the same role check so an
 * unauthorized operator never sees a control that would just 403.
 */
export function useTenderPipeline() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { pipelineId: string; body: TenderRequestBody }) =>
      jsonFetch<FreightPipeline>(`/api/freight/pipeline/${vars.pipelineId}/tender`, {
        method: 'POST',
        body: vars.body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_KEY })
      qc.invalidateQueries({ queryKey: ['logistics', 'shipments'] })
      qc.invalidateQueries({ queryKey: ['logistics', 'shipment-detail'] })
    },
  })
}

/**
 * Starts a new pipeline row for a PO — the entry point that was missing
 * entirely until this fix (nothing in the ops UI ever called this route).
 * Idempotent server-side: calling this twice for the same `odoo_po_id`
 * while a non-terminal row already exists returns that row instead of
 * creating a duplicate (see app/services/freight_pipeline.py).
 */
export function useCreatePipeline() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreatePipelineBody) =>
      jsonFetch<FreightPipeline>('/api/freight/pipeline', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_KEY })
    },
  })
}

/**
 * Lightweight PO source for the "New quote" picker (Logistics-side pipeline
 * creation). Independently hits the same open-POs report the Procurement
 * module's `useVendorPOs` uses — deliberately NOT imported from
 * `@/modules/procurement` (cross-module import ban); see
 * `FreightStatusCell.tsx`'s docstring for the established precedent of
 * small, deliberate duplication over a shared abstraction here.
 *
 * v1 scope is intentionally just *open* POs (a PO needing a new freight
 * quote is, by definition, still open/unreceived) — not a full PO search.
 * The report returns one row per PO *line*; dedupe to one entry per po_id.
 */
export function useOpenPOsForPicker() {
  return useQuery({
    queryKey: ['logistics', 'open-po-picker'],
    queryFn: () => jsonFetch<{ rows: RawVendorPORow[] }>('/api/reports/vendor-pos'),
    staleTime: 30_000,
    select: (data) => dedupeOpenPOs(data.rows),
  })
}

function dedupeOpenPOs(rows: RawVendorPORow[]): OpenPOOption[] {
  const seen = new Map<number, OpenPOOption>()
  for (const r of rows) {
    if (!seen.has(r.po_id)) {
      seen.set(r.po_id, { po_id: r.po_id, po_number: r.po_number, vendor: r.vendor })
    }
  }
  return [...seen.values()]
}

export interface RegisterExternalShipmentBody {
  odoo_po_name: string
  carrier_name: string
  provider: string
  provider_ref: string
  bol_number?: string
  pickup_date?: string
  est_delivery?: string
  notes?: string
}

export interface RegisterExternalShipmentResult {
  shipment_id: number
  status: string
  live_tracking: boolean
}

/** Seed a freight row for a load booked externally (not via the ops tender flow). */
export function useRegisterExternalShipment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: RegisterExternalShipmentBody) =>
      jsonFetch<RegisterExternalShipmentResult>('/api/freight/register-external', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['logistics', 'shipments'] })
      qc.invalidateQueries({ queryKey: PIPELINE_KEY })
    },
  })
}

export interface ManualStatusUpdateBody {
  status: FreightStatus
  note?: string
  actual_pickup?: string
  actual_delivery?: string
}

/** Manual status update for provider='manual' or stale-API shipments. */
export function useManualStatusUpdate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ shipmentId, body }: { shipmentId: number; body: ManualStatusUpdateBody }) =>
      jsonFetch<FreightShipmentDetail>(`/api/freight/shipments/${shipmentId}/manual-status`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['logistics', 'shipments'] })
      qc.invalidateQueries({ queryKey: ['logistics', 'shipment-detail'] })
    },
  })
}

